import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';

import { useGetStripeCredentialEnvironment } from '~entities/stripe/api/stripe-credential-environment';
import { useGetStudioDefaultPaymentProvider } from '~entities/studios/api/get-studio-default-payment-provider';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { cn } from '~shared/shadcn/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';

export const StripeEnvironmentBanner = () => {
  const { data, isLoading } = useGetStripeCredentialEnvironment();
  const { data: studioPaymentProvider, isLoading: isProviderLoading } =
    useGetStudioDefaultPaymentProvider();
  const { hasSuperAdminAccess } = useCurrentUserRole();

  const isSquareProvider = studioPaymentProvider?.paymentProvider === 'square';

  if (isLoading || isProviderLoading || !data?.showBanner || !data.reason) {
    return null;
  }

  // missing_keys comes from the Stripe credential API. Square studios have no
  // Stripe keys by design, including after a Square disconnect that leaves
  // paymentProvider as square.
  if (data.reason === 'missing_keys' && isSquareProvider) {
    return null;
  }

  const isMissingKeys = data.reason === 'missing_keys';
  const isAmberWarning =
    data.reason === 'test_mode' || data.reason === 'key_mode_mismatch';

  const title = isMissingKeys
    ? 'Payment provider is not set up'
    : data.reason === 'test_mode'
      ? 'Stripe test environment'
      : 'Stripe key mismatch';

  const body = isMissingKeys
    ? 'A payment provider is not connected for this studio yet. Set one up under Payment integrations before you can process online payments through Enrollio.'
    : data.reason === 'test_mode'
      ? 'You are operating with Stripe test keys. To accept real payments, connect with Stripe Connect or switch to live keys in your Stripe dashboard.'
      : 'Your publishable and secret keys do not match the same Stripe mode (for example test vs live), or one could not be recognized. Parent checkout and card flows can fail until this is fixed.';

  return (
    <Alert
      className={cn(
        'mb-4 [&>svg]:text-foreground',
        isMissingKeys &&
          'border-red-300 bg-red-50 text-red-950 [&>svg]:text-red-800',
        isAmberWarning &&
          'border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-800',
      )}
    >
      <AlertTriangle className='size-4' />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription
        className={cn(
          'space-y-2',
          isMissingKeys ? 'text-red-900' : 'text-amber-900',
        )}
      >
        <p>{body}</p>
        {hasSuperAdminAccess ? (
          <p>
            <Link
              to='/payment/payment-integration'
              className={cn(
                'font-semibold underline underline-offset-2',
                isMissingKeys ? 'hover:text-red-950' : 'hover:text-amber-950',
              )}
            >
              Open payment integrations
            </Link>{' '}
            {isMissingKeys
              ? 'to set up a payment provider.'
              : 'to review your setup.'}
          </p>
        ) : (
          <p className='text-sm'>
            Ask a super admin to set up a provider under Payment → Payment
            integrations.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
};
